import { type FormEvent, type ReactNode, useId, useRef, useState } from "react";

import { type Registration, type RegistrationField, validateRegistration } from "../../shared";
import type { RegistrationState } from "../survey";
import { cx } from "../ui";
import { PAPERNAUT_URL } from "../webinar";

type Field = {
	name: RegistrationField;
	label: string;
	type: "text" | "email";
	autoComplete: string;
	maxLength: number;
	required?: boolean;
};

const FIELDS: Field[] = [
	{ name: "firstName", label: "Vorname", type: "text", autoComplete: "given-name", maxLength: 80, required: true },
	{ name: "lastName", label: "Nachname", type: "text", autoComplete: "family-name", maxLength: 80, required: true },
	{ name: "email", label: "E-Mail-Adresse", type: "email", autoComplete: "email", maxLength: 254 },
];

type Props = {
	state: RegistrationState;
	connected: boolean;
	onRegister: (registration: Registration) => void;
	/** Shown next to the submit button. */
	children?: ReactNode;
};

export function RegistrationForm({ state, connected, onRegister, children }: Props) {
	const id = useId();
	const [values, setValues] = useState<Registration>({ firstName: "", lastName: "", email: "" });
	const [attempted, setAttempted] = useState(false);
	const inputs = useRef<Partial<Record<RegistrationField, HTMLInputElement | null>>>({});

	const pending = state.status === "pending";
	// Once submitted, errors follow the input so they disappear as soon as a field is fixed.
	const errors = (attempted ? validateRegistration(values).errors : null) ??
		(state.status === "rejected" ? state.errors : {});

	const submit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (pending) {
			return;
		}
		setAttempted(true);

		const result = validateRegistration(values);
		if (result.registration) {
			onRegister(result.registration);
			return;
		}
		const firstInvalid = FIELDS.find((field) => result.errors[field.name]);
		if (firstInvalid) {
			inputs.current[firstInvalid.name]?.focus();
		}
	};

	return (
		<form className="mt-8" noValidate onSubmit={submit}>
			<div className="grid gap-4 sm:grid-cols-2">
				{FIELDS.map((field) => {
					const inputId = `${id}-${field.name}`;
					const error = errors[field.name];
					const isEmail = field.type === "email";
					return (
						<div key={field.name} className={cx("grid content-start gap-1.5", isEmail && "sm:col-span-2")}>
							<label htmlFor={inputId} className="field-label">
								{field.label}
							</label>
							<input
								ref={(element) => {
									inputs.current[field.name] = element;
								}}
								id={inputId}
								name={field.name}
								type={field.type}
								className="field-input"
								autoComplete={field.autoComplete}
								autoCapitalize={isEmail ? "none" : "words"}
								inputMode={isEmail ? "email" : undefined}
								enterKeyHint={isEmail ? "go" : "next"}
								spellCheck={false}
								maxLength={field.maxLength}
								required={field.required}
								readOnly={pending}
								value={values[field.name]}
								aria-invalid={error ? true : undefined}
								aria-describedby={error ? `${inputId}-error` : undefined}
								onChange={(event) => {
									const { value } = event.target;
									setValues((current) => ({ ...current, [field.name]: value }));
								}}
							/>
							{error && (
								<p id={`${inputId}-error`} className="field-error">
									{error}
								</p>
							)}
						</div>
					);
				})}
			</div>

			<div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
				<button type="submit" className="btn-launch px-6 py-3 text-base" disabled={pending}>
					{pending ? (
						"Einen Moment …"
					) : (
						<>
							Mission starten
							<span aria-hidden="true">🚀</span>
						</>
					)}
				</button>
				{children}
			</div>

			<p className="mt-3 min-h-5 text-sm text-yellow" role="status">
				{pending && !connected ? "Verbindung wird aufgebaut – Ihre Anmeldung wird gleich gesendet." : ""}
			</p>

			<p className="mt-2 text-xs leading-relaxed text-ice/70">
				Mit dem Start speichern wir Ihren Namen, Ihre E-Mail-Adresse und Ihre Antworten zur Auswertung dieses
				Webinars. Im Webinar zeigen wir nur Gesamtergebnisse – niemand sieht, wer was geantwortet hat.{" "}
				<a
					className="underline underline-offset-2 hover:text-white"
					href={`${PAPERNAUT_URL}datenschutz/`}
					target="_blank"
					rel="noopener"
				>
					Datenschutz
				</a>
			</p>
		</form>
	);
}
